import type { ReactNode } from 'react';
import { Info, MapPin, Sun, X } from '../../icons';
import type { WeatherLocation } from '../../api/weather';
import { weatherLocationCopy } from '../../lib/weather-location';
import { tempText, type WeatherView } from '../../lib/weather-view';
import './WeatherPanel.css';

/**
 * 날씨 — **부품 하나**, 겉모양만 둘 (2026-09-21 · 시안 v4 확정).
 *
 *   · `strip`  — 대시보드 맨 위 **고정 줄**(위젯이 아니다 · 끌거나 지울 수 없다)
 *   · `widget` — 보드에 담는 **위젯**(폭 1/3 기본 · 정본 「공통 상태」 규격)
 *
 * ★★★ 둘을 다른 부품으로 만들지 않는다. 상태 판정과 문구가 두 벌이 되면 반드시 어긋난다 —
 *      말은 이미 `lib/weather-location.ts` 한 곳이고, 값 해석은 `lib/weather-view.ts` 한 곳이다.
 *      여기서 다른 것은 **담는 틀**뿐이다.
 *
 * ## 상태 넷 — 앞의 둘은 «위치», 셋째는 «값», 넷째는 «기다림»
 *
 *   | 상태        | 무엇을 말하나                     | 누가 고치나 |
 *   |-------------|-----------------------------------|-------------|
 *   | `ready`     | 날씨                              | — |
 *   | `location`  | 위치를 알 수 없음 · 주소 없음     | 관리자(주소) |
 *   | `failed`    | 값을 못 가져옴(공급자 · 공유 한도) | 기다리면 된다 |
 *   | `loading`   | 불러오는 중                       | — |
 *
 * ⚠ `location` 과 `failed` 를 뭉치지 않는다 — 「주소를 고쳐라」와 「잠시 뒤 다시」는 다른 일이다.
 */

export type WeatherItemKey = 'feels' | 'humidity' | 'pop' | 'wind' | 'hours';

/** 시안 v4 의 기본값 — 시간별 예보는 꺼 둔다(호출도 줄어든다). */
export const WEATHER_ITEMS_DEFAULT: readonly WeatherItemKey[] = ['feels', 'humidity', 'pop', 'wind'];

export type WeatherPanelState =
  | { kind: 'loading' }
  | { kind: 'ready'; regionName: string; view: WeatherView }
  | { kind: 'location'; location: WeatherLocation; place: 'company' | 'worksite' }
  | { kind: 'failed'; regionName: string | null };

export interface WeatherPanelProps {
  variant: 'strip' | 'widget';
  state: WeatherPanelState;
  /** 보이는 항목(위젯 설정). 고정 줄은 기본값을 쓴다. */
  items?: readonly WeatherItemKey[];
  /** 주소를 고칠 수 있는 사람인가 — 그 사람에게만 「회사정보 열기」가 붙는다. */
  canEditAddress?: boolean;
  onOpenCompany?: () => void;
  onRetry?: () => void;
}

function Stat({ label, value }: { label: string; value: string }) {
  return <span className="wxp__stat"><span>{label}</span><b>{value}</b></span>;
}

export function WeatherPanel({
  variant, state, items = WEATHER_ITEMS_DEFAULT, canEditAddress = false, onOpenCompany, onRetry,
}: WeatherPanelProps) {
  const has = (k: WeatherItemKey) => items.includes(k);
  const cls = `wxp wxp--${variant}`;

  if (state.kind === 'loading') {
    return (
      <div className={`${cls} wxp--skel`} role="status" aria-label="날씨 불러오는 중">
        <i /><i /><i />
      </div>
    );
  }

  if (state.kind === 'location') {
    const copy = weatherLocationCopy(state.location, state.place);
    const Icon = state.location.kind === 'no_address' ? Info : MapPin;
    return (
      <div className={`${cls} wxp--state`}>
        <Icon className="wxp__stateicon" aria-hidden />
        <div className="wxp__msg">
          <b>{state.location.kind === 'no_address' ? '회사 주소가 없습니다' : '위치를 알 수 없습니다'}</b>
          <span>{canEditAddress ? copy.hint : `${copy.hint} 관리자에게 알려 주십시오.`}</span>
        </div>
        {canEditAddress && onOpenCompany && (
          <button type="button" className="wxp__btn wxp__btn--primary" onClick={onOpenCompany}>회사정보 열기</button>
        )}
      </div>
    );
  }

  if (state.kind === 'failed') {
    return (
      <div className={`${cls} wxp--state`}>
        <X className="wxp__stateicon" aria-hidden />
        <div className="wxp__msg">
          <b>날씨를 가져오지 못했습니다</b>
          <span>잠시 뒤 다시 보입니다.</span>
        </div>
        {state.regionName && <span className="wxp__where wxp__where--quiet">{state.regionName}</span>}
        {onRetry && <button type="button" className="wxp__btn" onClick={onRetry}>다시 시도</button>}
      </div>
    );
  }

  const { now, hours } = state.view;
  const stats: ReactNode[] = [];
  // ★ 체감은 «기온과 다를 때만» 보여준다. 봄·가을에는 기온 그대로라, 같은 숫자를 두 번 적으면
  //   사람이 「뭐가 다르지」를 찾다가 아무것도 못 얻는다(시험이 잡은 자리).
  const feelsDiffers = now.feelsC !== null && now.tempC !== null && Math.round(now.feelsC) !== Math.round(now.tempC);
  if (has('feels') && feelsDiffers) stats.push(<Stat key="feels" label="체감" value={tempText(now.feelsC)} />);
  if (has('humidity') && now.humidity !== null) stats.push(<Stat key="hum" label="습도" value={`${now.humidity}%`} />);
  if (has('pop') && now.pop !== null) stats.push(<Stat key="pop" label="강수" value={`${now.pop}%`} />);
  if (has('wind') && now.windMs !== null) stats.push(<Stat key="wind" label="바람" value={`${now.windMs}m/s`} />);

  return (
    <div className={cls}>
      <span className="wxp__icon" aria-hidden><Sun /></span>
      <span className="wxp__now">
        <b className="wxp__temp">{tempText(now.tempC)}</b>
        {now.sky && <span className="wxp__sky">{now.sky}</span>}
        <span className="wxp__where"><MapPin aria-hidden />{state.regionName}</span>
      </span>
      {stats.length > 0 && <span className="wxp__stats">{stats}</span>}
      {has('hours') && hours.length > 0 && (
        <span className="wxp__hours">
          {hours.map((h) => (
            <span key={h.label} className="wxp__hour">
              <span>{h.label}</span>
              <b>{tempText(h.tempC)}</b>
              {h.pop !== null && <span>{h.pop}%</span>}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
